import type { ThemeConfig } from '@ai-tutor/types';

// Default theme configuration
export const defaultTheme: ThemeConfig = {
  // Inert: ThemeProvider strips `mode` from this object so a default can
  // never clobber a saved preference. Kept in step with useTheme anyway.
  mode: 'system',
  primaryColor: '#3b82f6', // Blue-500
  secondaryColor: '#64748b', // Slate-500
  // Kept in step with the `sans` stack in tailwind.config.js, which is what
  // actually renders text (via the `body` rule in globals.css).
  fontFamily: "'Inter Variable', 'Inter', system-ui, sans-serif",
  borderRadius: 8,
  customVariables: {
    '--header-height': '64px',
    '--sidebar-width': '280px',
    '--content-max-width': '1200px',
    '--spacing-unit': '0.25rem',
  },
};

// Theme presets
export const themePresets: Record<string, Partial<ThemeConfig>> = {
  default: defaultTheme,
  dark: {
    ...defaultTheme,
    mode: 'dark',
  },
  blue: {
    ...defaultTheme,
    primaryColor: '#2563eb', // Blue-600
    secondaryColor: '#475569', // Slate-600
  },
  green: {
    ...defaultTheme,
    primaryColor: '#059669', // Emerald-600
    secondaryColor: '#4b5563', // Gray-600
  },
  purple: {
    ...defaultTheme,
    primaryColor: '#7c3aed', // Violet-600
    secondaryColor: '#6366f1', // Indigo-500
  },
  compact: {
    ...defaultTheme,
    borderRadius: 4,
    customVariables: {
      ...defaultTheme.customVariables,
      '--spacing-unit': '0.125rem',
    },
  },
  rounded: {
    ...defaultTheme,
    borderRadius: 12,
  },
};

// Theme validation
export const validateTheme = (theme: Partial<ThemeConfig>): string[] => {
  const errors: string[] = [];

  if (theme.primaryColor && !/^#[0-9A-Fa-f]{6}$/.test(theme.primaryColor)) {
    errors.push('Primary color must be a valid hex color (e.g., #3b82f6)');
  }

  if (theme.secondaryColor && !/^#[0-9A-Fa-f]{6}$/.test(theme.secondaryColor)) {
    errors.push('Secondary color must be a valid hex color (e.g., #64748b)');
  }

  if (
    theme.borderRadius &&
    (theme.borderRadius < 0 || theme.borderRadius > 20)
  ) {
    errors.push('Border radius must be between 0 and 20 pixels');
  }

  if (theme.mode && !['light', 'dark', 'system'].includes(theme.mode)) {
    errors.push('Theme mode must be "light", "dark", or "system"');
  }

  return errors;
};

// Theme utilities
export const getThemePreset = (presetName: string): Partial<ThemeConfig> => {
  return themePresets[presetName] || themePresets.default;
};

export const mergeThemes = (
  base: ThemeConfig,
  override: Partial<ThemeConfig>
): ThemeConfig => {
  return {
    ...base,
    ...override,
    customVariables: {
      ...base.customVariables,
      ...override.customVariables,
    },
  };
};
